import { useSessionStorage } from "@mantine/hooks";
import { useCallback, useEffect, useMemo } from "react";

const useExpanded = (storageKey: string, allItemKeys?: string[] | undefined): [
    string[], 
    (expanded: string[]) => void, 
    (key: string) => void,
    (key: string, expanded: boolean) => void
] => {
    const [expanded, setExpanded] = useSessionStorage<Record<string, boolean>>({ key: storageKey, defaultValue: {} });
    
    useEffect(() => {
        if (!allItemKeys) {
            return;
        }

        setExpanded(current => {
            const newValue = {...current}

            for (let itemKey of allItemKeys) {
                if (newValue[itemKey] === undefined) {
                    newValue[itemKey] = true;
                }
            }

            return newValue;
        })
    }, [setExpanded, allItemKeys]);

    const expandedKeys = useMemo(() => Object.keys(expanded).filter(e => expanded[e]), [expanded]);

    const setExpandedKeys = useCallback((expanded: string[]) => {
        setExpanded(current => {
            const newValue = {...current};

            for (let key in newValue) {
                newValue[key] = false;
            }

            for (let key of expanded) {
                newValue[key] = true;
            }

            return newValue;
        });
    }, [setExpanded])

    const toggleKeyExpanded = useCallback((key: string) => {
        setExpanded(current => {
            const newValue = {...current};

            newValue[key] = !current[key];

            return newValue;
        })
    }, [setExpanded]);

    const setKeyExpanded = useCallback((key: string, expanded: boolean) => {
        setExpanded(current => {
            if (expanded && current[key]) {
                return current;
            }
            
            if (!expanded && !current[key]) {
                return current;
            }

            const newValue = {...current};

            newValue[key] = expanded;

            return newValue;
        })
    }, [setExpanded]);

    return [expandedKeys, setExpandedKeys, toggleKeyExpanded, setKeyExpanded] ;
}

export default useExpanded